<template>
  <div>
    <p class="tw-text-muted tw-mb-4 tw-text-sm">
      Sales form on <strong>mlsingest.com/contact</strong> lands here.
      New inquiries show until you mark them read or closed.
    </p>

    <Message v-if="!warehouseConfigured" severity="warn" :closable="false" class="tw-mb-4">
      Set <code>LISTINGS_SUPABASE_URL</code> and <code>LISTINGS_SUPABASE_SERVICE_ROLE_KEY</code>
      in <code>.env</code>, then paste <code>docs/contracts/005_inquiries.sql</code> in the
      listings Supabase SQL editor.
    </Message>

    <div class="kpi-row tw-mb-4">
      <div class="kpi-card">
        <div class="kpi-label">New</div>
        <div class="kpi-value">{{ countByStatus('new') }}</div>
        <div class="kpi-hint">Unread from the site</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Read</div>
        <div class="kpi-value">{{ countByStatus('read') }}</div>
        <div class="kpi-hint">Opened, not closed</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Closed</div>
        <div class="kpi-value">{{ countByStatus('closed') }}</div>
        <div class="kpi-hint">Done</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">All</div>
        <div class="kpi-value">{{ allRows.length }}</div>
        <div class="kpi-hint">In this filter</div>
      </div>
    </div>

    <Card class="tw-mb-4">
      <template #title>Filters</template>
      <template #content>
        <div class="filters">
          <InputText
            v-model="q"
            class="tw-w-full"
            placeholder="Search name, email, brokerage, board"
            @input="onFilterInput"
            @keydown.enter.native.prevent="applyFiltersNow"
          />
          <Dropdown
            v-model="statusFilter"
            :options="statusFilterOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Status"
            class="tw-w-full"
            @change="load"
          />
          <Button label="Refresh" icon="pi pi-refresh" :loading="loading" @click="load" />
        </div>
      </template>
    </Card>

    <Message v-if="error" severity="error" :closable="false" class="tw-mb-4">{{ error }}</Message>
    <Message v-if="actionMessage" :severity="actionOk ? 'success' : 'warn'" :closable="true" class="tw-mb-4">
      {{ actionMessage }}
    </Message>

    <Card>
      <template #title>Inquiries</template>
      <template #subtitle>mlsingest.com contact form</template>
      <template #content>
        <DataTable
          :value="rows"
          :loading="loading"
          dataKey="id"
          :paginator="true"
          :rows="20"
          responsiveLayout="scroll"
          emptyMessage="No inquiries in this filter."
        >
          <Column header="When" style="min-width: 9rem">
            <template #body="{ data }">
              <div class="cell-stack">
                <span>{{ formatWhen(data.createdAt) }}</span>
                <span class="tw-text-muted tw-text-xs">{{ relativeWhen(data.createdAt) }}</span>
              </div>
            </template>
          </Column>
          <Column header="Status" style="min-width: 6.5rem">
            <template #body="{ data }">
              <span class="status-pill" :data-status="data.status">{{ data.status }}</span>
            </template>
          </Column>
          <Column header="Who" style="min-width: 12rem">
            <template #body="{ data }">
              <div class="cell-stack">
                <strong>{{ data.name || '—' }}</strong>
                <a v-if="data.email" class="tw-text-xs" :href="'mailto:' + data.email">{{ data.email }}</a>
              </div>
            </template>
          </Column>
          <Column header="Brokerage" style="min-width: 10rem">
            <template #body="{ data }">
              <div class="cell-stack">
                <span>{{ data.company || '—' }}</span>
                <span class="tw-text-muted tw-text-xs">{{ intentLabel(data.intent) }}</span>
              </div>
            </template>
          </Column>
          <Column header="Board" style="min-width: 8rem">
            <template #body="{ data }">{{ data.board || '—' }}</template>
          </Column>
          <Column header="" style="min-width: 7rem">
            <template #body="{ data }">
              <Button
                type="button"
                label="Open"
                class="p-button-text p-button-sm"
                @click.stop="openDetail(data)"
              />
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>

    <Dialog
      :visible.sync="detailVisible"
      modal
      dismissableMask
      closable
      :style="{ width: 'min(40rem, 96vw)' }"
      :header="selected ? selected.name : 'Inquiry'"
      @hide="selected = null"
    >
      <div v-if="selected">
        <div class="detail-grid">
          <div>
            <span class="lbl">Status</span>
            <span class="status-pill" :data-status="selected.status">{{ selected.status }}</span>
          </div>
          <div>
            <span class="lbl">When</span>
            {{ formatWhen(selected.createdAt) }}
          </div>
          <div>
            <span class="lbl">Email</span>
            <a :href="'mailto:' + selected.email">{{ selected.email }}</a>
          </div>
          <div>
            <span class="lbl">Phone</span>
            {{ selected.phone || '—' }}
          </div>
          <div>
            <span class="lbl">Brokerage</span>
            {{ selected.company || '—' }}
          </div>
          <div>
            <span class="lbl">Role</span>
            {{ selected.role || '—' }}
          </div>
          <div>
            <span class="lbl">Need</span>
            {{ intentLabel(selected.intent) }}
          </div>
          <div>
            <span class="lbl">Board</span>
            {{ selected.board || '—' }}
          </div>
          <div class="span-2">
            <span class="lbl">Message</span>
            <p class="inquiry-body">{{ selected.message || '—' }}</p>
          </div>
        </div>
      </div>
      <template #footer>
        <Button label="Close dialog" class="p-button-text" @click="detailVisible = false" />
        <Button
          v-if="selected && selected.status !== 'read'"
          label="Mark read"
          class="p-button-outlined"
          :loading="acting"
          @click="setStatus('read')"
        />
        <Button
          v-if="selected && selected.status !== 'closed'"
          label="Mark closed"
          :loading="acting"
          @click="setStatus('closed')"
        />
        <Button
          v-if="selected && selected.email"
          label="Reply"
          icon="pi pi-envelope"
          class="p-button-outlined"
          @click="reply"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import Card from 'primevue/card'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Dropdown from 'primevue/dropdown'
import Message from 'primevue/message'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Dialog from 'primevue/dialog'

const INTENT_LABELS = {
  board: 'Get a board live',
  licensing: 'Licensing / data agreement',
  website: 'Brokurz website MLS',
  operator: 'Operator / API access',
  other: 'Something else',
}

const STATUS_QUERY = gql`
  query ListingsWarehouseStatus {
    listingsWarehouseStatus { configured }
  }
`

const LIST_QUERY = gql`
  query SalesInquiries($status: InquiryStatus, $q: String) {
    salesInquiries(status: $status, q: $q) {
      id firstName lastName name email phone company role intent board
      message status source createdAt updatedAt readAt closedAt
    }
  }
`

const SET_STATUS = gql`
  mutation SetSalesInquiryStatus($id: ID!, $status: InquiryStatus!) {
    setSalesInquiryStatus(id: $id, status: $status) {
      id status readAt closedAt updatedAt
    }
  }
`

export default {
  name: 'InquiriesPage',
  components: { Card, Button, InputText, Dropdown, Message, DataTable, Column, Dialog },
  data() {
    return {
      rows: [],
      allRows: [],
      loading: false,
      acting: false,
      error: null,
      actionMessage: null,
      actionOk: false,
      warehouseConfigured: false,
      q: '',
      statusFilter: 'new',
      selected: null,
      detailVisible: false,
      filterTimer: null,
      statusFilterOptions: [
        { label: 'New', value: 'new' },
        { label: 'Read', value: 'read' },
        { label: 'Closed', value: 'closed' },
        { label: 'All', value: null },
      ],
    }
  },
  mounted() {
    this.bootstrap()
  },
  beforeDestroy() {
    if (this.filterTimer) clearTimeout(this.filterTimer)
  },
  methods: {
    intentLabel(value) {
      if (!value) return '—'
      return INTENT_LABELS[value] || value
    },
    countByStatus(status) {
      return this.allRows.filter((row) => row.status === status).length
    },
    formatWhen(iso) {
      if (!iso) return '—'
      try {
        return new Date(iso).toLocaleString()
      } catch (err) {
        return iso
      }
    },
    relativeWhen(iso) {
      if (!iso) return ''
      const then = new Date(iso).getTime()
      if (!Number.isFinite(then)) return ''
      const mins = Math.round((Date.now() - then) / 60000)
      if (mins < 1) return 'just now'
      if (mins < 60) return mins + 'm ago'
      const hours = Math.round(mins / 60)
      if (hours < 48) return hours + 'h ago'
      const days = Math.round(hours / 24)
      return days + 'd ago'
    },
    onFilterInput() {
      if (this.filterTimer) clearTimeout(this.filterTimer)
      this.filterTimer = setTimeout(this.applyFiltersNow, 280)
    },
    applyFiltersNow() {
      this.load()
    },
    async bootstrap() {
      try {
        const res = await this.$apollo.query({ query: STATUS_QUERY, fetchPolicy: 'network-only' })
        this.warehouseConfigured = Boolean(res.data && res.data.listingsWarehouseStatus && res.data.listingsWarehouseStatus.configured)
      } catch (err) {
        this.warehouseConfigured = false
      }
      await this.load()
    },
    async load() {
      this.loading = true
      this.error = null
      try {
        const res = await this.$apollo.query({
          query: LIST_QUERY,
          variables: { status: this.statusFilter, q: this.q || null },
          fetchPolicy: 'network-only',
        })
        this.rows = (res.data && res.data.salesInquiries) || []
        const all = await this.$apollo.query({
          query: LIST_QUERY,
          variables: { status: null, q: null },
          fetchPolicy: 'network-only',
        })
        this.allRows = (all.data && all.data.salesInquiries) || []
      } catch (err) {
        this.error = err.message || 'Failed to load inquiries'
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    async openDetail(row) {
      this.selected = row
      this.detailVisible = true
      if (row.status === 'new') {
        try {
          await this.setStatus('read', true)
        } catch (err) {
          /* keep dialog open */
        }
      }
    },
    async setStatus(status, silent) {
      if (!this.selected) return
      this.acting = true
      this.actionMessage = null
      try {
        const res = await this.$apollo.mutate({
          mutation: SET_STATUS,
          variables: { id: this.selected.id, status },
        })
        const next = res.data && res.data.setSalesInquiryStatus
        if (next) {
          this.selected = { ...this.selected, ...next }
        }
        if (!silent) {
          this.actionOk = true
          this.actionMessage = 'Marked ' + status + '.'
        }
        await this.load()
      } catch (err) {
        this.actionOk = false
        this.actionMessage = err.message || 'Could not update'
      } finally {
        this.acting = false
      }
    },
    reply() {
      if (!this.selected || !this.selected.email) return
      const subject = encodeURIComponent('MLS Ingest')
      window.location.href = 'mailto:' + this.selected.email + '?subject=' + subject
    },
  },
}
</script>

<style scoped>
.kpi-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}
.kpi-card {
  border: 1px solid var(--bk-line, #e4e4e7);
  border-radius: 12px;
  background: #fff;
  padding: 0.85rem 1rem;
}
.kpi-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #71717a;
}
.kpi-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #18181b;
  line-height: 1.2;
}
.kpi-hint {
  font-size: 0.75rem;
  color: #a1a1aa;
}
.filters {
  display: grid;
  grid-template-columns: 1fr 12rem auto;
  gap: 0.75rem;
  align-items: center;
}
.cell-stack {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: #f4f4f5;
  color: #3f3f46;
}
.status-pill[data-status='new'] {
  background: #fff7ed;
  color: #c2410c;
}
.status-pill[data-status='read'] {
  background: #eff6ff;
  color: #1d4ed8;
}
.status-pill[data-status='closed'] {
  background: #ecfdf5;
  color: #047857;
}
.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem 1.25rem;
  font-size: 0.875rem;
}
.detail-grid .lbl {
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #71717a;
  margin-bottom: 0.15rem;
}
.detail-grid .span-2 {
  grid-column: 1 / -1;
}
.inquiry-body {
  margin: 0;
  white-space: pre-wrap;
  line-height: 1.5;
}
@media (max-width: 860px) {
  .kpi-row,
  .filters,
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
